// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { BoardParametersV2 } from '../../../model/board-parameters';
import { TargetWeights } from '../../../model/target-balancing/target-weights';
import { CCCModelUtility } from '../ccc-model-utility/ccc-model-utility';
import { CarbonCausalChain } from './carbon-causal-chain/carbon-causal-chain';
import { KeyMap } from './key-map';
import { IClimateCortexModelService } from './model-service';
import {
  ParameterTransition,
  SupplyTransitionData,
  TransitionData,
  TransitionElementCosts,
  TransitionResourceShiftData,
  TransitionShiftData,
  TransitionUpdateData,
  TransitionUpdateDirection,
} from './transition-data';
import { TransitionTarget } from './transition-target/transition-target';
import { Variable } from './variable/variable';

import { formatUnitAsSnakeCase } from '../../../functions/units/format-unit-as-snake-case';
import { TransitionElement } from '../../../model/enums/transition-element';
import { TracingService } from '../../tracing-service/tracing.service';
import {
  CalculationResult,
  CccData,
  ResourceData,
  SubsectorEmissionData,
  VariableData,
  WorkData,
  WorkOutputData,
  WorkResourceData,
} from '../model/calculation-result';
import { ICarbonCausalChain, Output, Work, WORK_TO_SKIP } from '../model/carbon-causal-chain';
import { EvaluatedFactor } from '../model/factor';
import { IGlobalExternality } from '../model/global-externality';
import { IResource, ResourceType } from '../model/resource';
import { IStock } from '../model/stock';
import { ITransitionElement, TransitionElementType } from '../model/transition-element';
import { getValueFromFactor, instantiateFactor } from '../utils/factor-util';
import { DISTRICT_HEAT, ELECTRICITY, Resource } from './resource/resource';
import { Stock } from './stock/stock';

export const DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM = [
  TransitionElement.MoreEfficientUseOfLivingArea,
  TransitionElement.EnergyEfficientNewCommercialBuildings,
  TransitionElement.EnergyEfficientNewHousing,
  TransitionElement.ReducedProductionOfWaste,
  TransitionElement.HydrogenBlendedInTheGasMixForHeatingBuildings,
  TransitionElement.LowCarbonConstructionOfBuildings,
];

const CITY_GAS_MIX = 'city_gas_mix'; // Needed for legacy reasons

/**
 * TODOS:
 *    - eval of parameter (parameter described as function in Airtable)
 *    - get rid of atoc_rhs dependencies in code for UPDATE's and UPSHIFT's
 * - DISCUSSIONS
 *    - what about sinks (both UX and model)
 *    - what about atoc, should it be bau or reference scenario, if bau, and in case of upshift, all activity will never be transitioned.
 * - AFTER MVP
 *    - finish pipeline, with generation of data from AirTable => Blob Storage
 *    - parameter in upshift needs to be altered negatively (e.g. efficiency) as a consequence of improving another parameter (e.g. load factor)
 */

@Injectable({
  providedIn: 'root',
})
export class CccAlgorithmService {
  private _cccIdsActive: KeyMap<Set<string>> = {};
  private _cccKeys: string[] = [];
  private _cccKeysForInventory: KeyMap<string[]> = {};
  private _cccs: KeyMap<CarbonCausalChain> = {};
  private _cccsForInventory: KeyMap<KeyMap<CarbonCausalChain>> = {};
  private _discountRate: number = 0;
  private _resources: KeyMap<Resource> = {};
  private _resourcesForInventory: KeyMap<KeyMap<Resource>> = {};
  private _resourceTtKeys: string[] = [];
  private _shiftTtKeys: string[] = [];
  private _stocks: KeyMap<Stock> = {};
  private _supplyAlterationTtKeys: string[] = [];
  private _supplyUpdateTtKeys: string[] = [];
  private _transitionElements: KeyMap<TransitionTarget> = {};
  private _ttKeys: string[] = [];
  private _updateTtKeys: string[] = [];
  private _upshiftsTtKeys: string[] = [];
  private _variables: KeyMap<Variable> = {};
  private _variablesForInventory: { [inventoryId: string]: KeyMap<Variable> } = {};

  private readonly _allCccs: KeyMap<ICarbonCausalChain>;
  private readonly _allGlobalExternalities: KeyMap<IGlobalExternality>;
  private readonly _allResources: KeyMap<IResource>;
  private readonly _allStocks: KeyMap<IStock>;
  private readonly _allTransitionElements: KeyMap<ITransitionElement>;

  /**
   * Compare function moving all transition elements producing electricity, before everything else.
   * @TODO once we have TEs that produce both district heat and electricity, those will need to come next in line. Extension of this function will be needed then.
   */
  private get _compareResourcesProduced(): (a: string, b: string) => number {
    return this._tracingService.trace('_compareResourcesProduced', () => {
      return (a, b) => {
        const resourcesProducedA = this._transitionElements[a].producedResources;
        const resourcesProducedB = this._transitionElements[b].producedResources;
        if (!resourcesProducedA && !resourcesProducedB) {
          return 0;
        } else if (!resourcesProducedA) {
          return !resourcesProducedB ? 0 : -1;
        } else if (resourcesProducedA.length === 1 && resourcesProducedA[0] === ELECTRICITY) {
          return resourcesProducedB.length === 1 && resourcesProducedB[0] === ELECTRICITY ? 0 : -1;
        }
        return 0;
      };
    });
  }

  private get _ttKeysInOrder(): string[] {
    return [...this._resourceTtKeys, ...this._updateTtKeys, ...this._upshiftsTtKeys, ...this._shiftTtKeys];
  }

  constructor(
    private _modelService: IClimateCortexModelService,
    private _cccModelUtility: CCCModelUtility,
    private _tracingService: TracingService
  ) {
    this._allCccs = this._modelService.carbonCausalChains;
    this._allGlobalExternalities = this._modelService.globalExternalities;
    this._allResources = this._modelService.resources;
    this._allTransitionElements = this._modelService.transitionElements;
    this._allStocks = this._modelService.stocks;
  }

  calculate(
    tSetup: number,
    tReport: number,
    transitionWeights: TargetWeights,
    calculateCccCosts?: boolean,
    calculateCostsForTEs?: TransitionElement[]
  ): CalculationResult {
    return this._tracingService.trace('calculate', () => {
      const calculationResult = this._calculateTransition(tSetup, tReport, transitionWeights);
      const subsectorEmissionData: KeyMap<SubsectorEmissionData> = this._getSubsectorDividedEmissionData(tSetup);
      let cccCosts = {};
      let teCosts = {};
      if (calculateCccCosts) {
        cccCosts = this._calculateCccCosts(tSetup, tReport);
      }
      if (calculateCostsForTEs?.length) {
        // costs that are positive are expenses, and negative are savings.
        teCosts = this._calculateTeCosts(tSetup, tReport, calculateCostsForTEs);
      }
      return { ...calculationResult, subsectorEmissionData, costs: { cccCosts, teCosts } };
    });
  }

  getCccEnergyEfficiencyForResourceAndInventoryId(inventoryId: string, cccId: string, resource: string): number {
    return this._tracingService.trace('getCccEnergyEfficiencyForResourceAndInventoryId', () => {
      return this._cccsForInventory[inventoryId][cccId].getEnergyEfficiencyFromResource(
        resource,
        this._variablesForInventory[inventoryId]
      );
    });
  }

  getCccEnergyIntensityForInventoryId(inventoryId: string, cccId: string): number {
    return this._tracingService.trace('getCccEnergyIntensityForInventoryId', () => {
      return this._cccsForInventory[inventoryId][cccId].calculateSimpleEnergyIntensityForCcc(
        this._variablesForInventory[inventoryId]
      );
    });
  }

  getCccEmissionBau(t: number): KeyMap<number> {
    return this._tracingService.trace('getCccEmissionBau', () => {
      return this._getCccEmissionsBau(t, this._variables);
    });
  }

  getCccEmissionsForInventoryId(inventoryId: string): KeyMap<number> {
    return this._tracingService.trace('getCccEmissionsForInventoryId', () => {
      return this._getCccEmissionsBauForInventory(inventoryId);
    });
  }

  getCccResourceEnergyConsumptionForInventoryId(inventoryId: string) {
    return this._tracingService.trace('getCccResourceEnergyConsumptionForInventoryId', () => {
      return this._getCccResourceEnergyConsumptionForInventory(inventoryId);
    });
  }

  getEmissionFactorsForInventoryId(inventoryId: string): KeyMap<number> {
    return this._tracingService.trace('getEmissionFactorsForInventoryId', () => {
      return this._getEmissionFactorsForInventory(inventoryId);
    });
  }

  getInitialPrimaryOutputDeliveredFromSupplyTE(transitionElement: TransitionElement): number | undefined {
    return this._tracingService.trace('getInitialPrimaryOutputDeliveredFromSupplyTE', () => {
      if (this._cccModelUtility.isTransitionElementSupplyAlteration(transitionElement)) {
        const initialOperations = this._cccs[this._transitionElements[transitionElement].lhsCcc[0]].initialOperations;
        const potoc = this._cccs[this._transitionElements[transitionElement].lhsCcc[0]].getPOtOC(this._variables);
        return initialOperations / potoc;
      }
    });
  }

  getInitialValueFromSupplyUpdateTE(transitionElement: TransitionElement): number | undefined {
    return this._tracingService.trace('getInitialValueFromSupplyUpdateTE', () => {
      if (this._cccModelUtility.isTransitionElementSupplyUpdate(transitionElement)) {
        return this._variables[this._transitionElements[transitionElement].supplyUpdateVariable].referenceValue;
      }
    });
  }

  init(
    variableData: BoardParametersV2,
    transitionTargetIdsToInstantiate?: TransitionElement[],
    variableDataForInventory?: { [inventoryId: string]: BoardParametersV2 },
    overridingEmissionFactors: KeyMap<string> = {}
  ): void {
    this._tracingService.trace('init', () => {
      transitionTargetIdsToInstantiate =
        transitionTargetIdsToInstantiate ??
        Object.keys(this._allTransitionElements).map((ttId) => <TransitionElement>String(ttId));
      const resourceNames = this._cccModelUtility.findResourcesToInstantiate(this._allCccs);
      this._instantiateParameters(variableData);
      this._discountRate = this._variables['discount_rate_general']?.value || 0;
      const overridingEmissionFactorsVariables: KeyMap<Variable> = {};
      Object.keys(overridingEmissionFactors).forEach((resource) => {
        const variable: Variable | undefined = this._variables[overridingEmissionFactors[resource]];
        if (variable) {
          overridingEmissionFactorsVariables[resource] = variable;
        }
      });
      this._instantiateResources([...resourceNames, CITY_GAS_MIX], overridingEmissionFactorsVariables);
      this._instantiateStocks();
      const supplySideActivities: string[] = this._cccModelUtility.getSupplySideActivities();
      this._instantiateCarbonCausalChains(supplySideActivities);
      this._instantiateTransitionElements(transitionTargetIdsToInstantiate);
      if (variableDataForInventory) {
        this._instantiateParametersForInventory(variableDataForInventory);
        this._instantiateResourcesForInventory([...resourceNames, CITY_GAS_MIX], overridingEmissionFactorsVariables);
        this._instantiateCarbonCausalChainsForInventory(supplySideActivities);
      }

      this._divideAndSetTransitions(this._ttKeys);
      this._populateCccIdSets();
    });
  }

  private _applySupplyTransitions(
    t: number,
    transitions: KeyMap<SupplyTransitionData>,
    transitionWeights: TargetWeights
  ): void {
    this._tracingService.trace('_applySupplyTransitions', () => {
      [...this._supplyUpdateTtKeys, ...this._supplyAlterationTtKeys].forEach((ttKey) => {
        transitions[ttKey] = this._transitionElements[ttKey].computeSupply(
          t,
          transitionWeights[ttKey],
          this._variables
        );
      });
    });
  }

  private _applyTransitions(
    tSetup: number,
    tReport: number,
    transitions: KeyMap<TransitionData>,
    transitionWeights: TargetWeights
  ) {
    this._tracingService.trace('_applyTransitions', () => {
      this._ttKeysInOrder.forEach((ttKey) => {
        transitions[ttKey] = this._transitionElements[ttKey].compute(
          tSetup,
          tReport,
          transitionWeights[ttKey],
          this._variables
        );
      });
    });
  }

  private _calculateCccCosts(tSetup: number, tReport: number): KeyMap<KeyMap<number>> {
    return this._tracingService.trace('_calculateCccCosts', () => {
      const costs = {};
      Object.keys(this._cccs).forEach((cccId) => {
        const cccCosts = this._cccs[cccId].calculateCosts(
          tSetup,
          tReport,
          this._variables,
          this._resources,
          this._stocks,
          this._allGlobalExternalities,
          1,
          this._discountRate
        );
        if (cccCosts) {
          costs[cccId] = {};
          cccCosts.forEach((cccCost) => {
            costs[cccId][cccCost.name] = cccCost.cost;
          });
        }
      });
      return costs;
    });
  }

  private _calculateTeCosts(
    tSetup: number,
    tReport: number,
    tes: TransitionElement[]
  ): KeyMap<TransitionElementCosts[]> {
    return this._tracingService.trace('_calculateTeCosts', () => {
      const costs = {};
      tes
        .filter((te) => this._cccModelUtility.canCalculateEconomicCase(te))
        .forEach((te) => {
          costs[te] = this._transitionElements[te].calculateCosts(
            tSetup,
            tReport,
            this._variables,
            this._resources,
            this._stocks,
            this._allGlobalExternalities,
            this._discountRate
          );
        });
      return costs;
    });
  }

  /**
   * 1. apply supply side transition operations
   * 2. objectify/calculate resource demand
   * 3. calculate all transitions
   *
   * @param tSetup
   * @param targetWeights
   * @returns
   */

  private _calculateTransition(
    tSetup: number,
    tReport: number,
    targetWeights: TargetWeights
  ): {
    transitions: KeyMap<TransitionData>;
    cccData: KeyMap<CccData>;
    supplyTransitions: KeyMap<SupplyTransitionData>;
    resources: KeyMap<ResourceData>;
    variables: KeyMap<VariableData>;
  } {
    return this._tracingService.trace('_calculateTransition', () => {
      const transitions: KeyMap<TransitionData> = {};
      const supplyTransitions: KeyMap<SupplyTransitionData> = {};

      this._resetModel(tSetup, tReport);
      this._applySupplyTransitions(tSetup, supplyTransitions, targetWeights);
      this._setupEnvironment(tSetup); // Initializing the CCCs, calculating demand and supply
      this._applyTransitions(tSetup, tReport, transitions, targetWeights);

      const variables: KeyMap<VariableData> = this._getVariablesData();
      const cccData: KeyMap<CccData> = this._getCccData(tSetup);
      const resources: KeyMap<ResourceData> = this._getResourcesData(this._resources);
      this._calculationAfterCortexFinish(tSetup, tReport, transitions, targetWeights, resources);

      return { transitions, cccData, supplyTransitions, resources, variables };
    });
  }

  /**
   * A second calculation round is needed for Transition Target Type Updates and ResourceShifts,
   * since we need to shift and update everything in the model, before a correct number can be calculated
   *
   * OBS: note that the order of the calculations performed here MUST cohore with the order calculations are done in `_applyTransitions`
   *
   * OBS. first invert all update changes, in order to get hold of correct carbon abatement values
   * since two updates on same parameter will affect abatement-output:
   * ttX: 50 % saving
   * ttY: 50 % efficiency improvement
   * total carbon abatement => 67 % ===== ( 1 - (1-0.5)*(1/1.5))
   * BUT if not reset ttX carbonabatement will be 0.33 and ttY will be 0.16
   *
   */
  private _calculationAfterCortexFinish(
    tSetup: number,
    tReport: number,
    transitions: KeyMap<TransitionData>,
    transitionWeights: TargetWeights,
    resources: KeyMap<any>
  ): void {
    this._tracingService.trace('_calculationAfterCortexFinish', () => {
      this._checkResourceDemand(tSetup, tReport, resources);
      this._invertResourceShiftChanges(tSetup, transitions, transitionWeights);
      this._ttKeysInOrder.reverse().forEach((ttKey) => {
        const weight = transitions[ttKey].newWeight || transitionWeights[ttKey].value;
        if (
          this._transitionElements[ttKey].transitionType === TransitionElementType.Update ||
          this._transitionElements[ttKey].transitionType === TransitionElementType.Upshift
        ) {
          this._setParameterRelatedValues(ttKey, tSetup, weight, transitions);
        } else if (this._transitionElements[ttKey].transitionType === TransitionElementType.ResourceShift) {
          const result = this._transitionElements[ttKey].computeAfterFinish(weight, this._variables);
          (<TransitionResourceShiftData>transitions[ttKey]).carbonAbatement.gross = result.grossCarbonAbatement;
          (<TransitionResourceShiftData>transitions[ttKey]).transitionedEmissions = result.transitionedEmissions;
          (<TransitionResourceShiftData>transitions[ttKey]).operationsAffected = result.operationsAffected;
        }
      });
      this._setPotential(tSetup, transitions, transitionWeights);
      this._setActivityToCarbonAbatementFactors(transitions);
    });
  }

  private _checkResourceDemand(tSetup: number, tReport: number, resources: KeyMap<ResourceData>) {
    this._tracingService.trace('_checkResourceDemand', () => {
      Object.keys(this._cccs).forEach((key) =>
        this._cccs[key].registerResourceDemand(tSetup, tReport, this._variables)
      );
      Object.keys(this._resources).forEach((key) => (resources[key].demand = this._resources[key].demand));
    });
  }

  private _divideAndSetTransitions(ttKeys: string[]): void {
    this._tracingService.trace('_divideAndSetTransitions', () => {
      this._updateTtKeys = [];
      this._resourceTtKeys = [];
      this._upshiftsTtKeys = [];
      this._shiftTtKeys = [];
      this._supplyAlterationTtKeys = [];
      this._supplyUpdateTtKeys = [];
      ttKeys.forEach((key) => {
        if (this._transitionElements[key].transitionType === TransitionElementType.SupplyAlteration) {
          this._supplyAlterationTtKeys.push(key);
        } else if (this._transitionElements[key].transitionType === TransitionElementType.SupplyUpdate) {
          this._supplyUpdateTtKeys.push(key);
        } else if (this._transitionElements[key].transitionType === TransitionElementType.Update) {
          this._updateTtKeys.push(key);
        } else if (this._transitionElements[key].transitionType === TransitionElementType.Shift) {
          this._shiftTtKeys.push(key);
        } else if (this._transitionElements[key].transitionType === TransitionElementType.Upshift) {
          this._upshiftsTtKeys.push(key);
        } else if (this._transitionElements[key].transitionType === TransitionElementType.ResourceShift) {
          this._resourceTtKeys.push(key);
        }
      });
      this._supplyAlterationTtKeys.sort(this._compareResourcesProduced);
    });
  }

  private _getCccData(t: number): KeyMap<CccData> {
    return this._tracingService.trace('_getCccData', () => {
      function getWorkToResourceCoefficient(output: Output, variables: KeyMap<Variable>): number {
        let workToResourceValue = 1;
        if (output.workToResource) {
          const workToResource = instantiateFactor(output.workToResource);
          workToResourceValue = getValueFromFactor(workToResource, variables);
        }
        return workToResourceValue;
      }

      const cccData: KeyMap<CccData> = {};
      Object.entries(this._allCccs)
        .filter((ccc) => ccc[1].work?.length > 0)
        .forEach((ccc) => {
          const operations: number = this._cccs[ccc[0]].operations;
          const consumedResources = {};
          const generatedResources = {};
          ccc[1].work
            .filter((work) => !WORK_TO_SKIP.includes(work.work))
            .forEach((work) => {
              const operationToWork = instantiateFactor(work.operationToWork);
              const operationsToWorkCoefficient: number = getValueFromFactor(operationToWork, this._variables);
              work.input.forEach((input) => {
                const proportion: number =
                  typeof input.resourceProportion === 'string'
                    ? this._variables[input.resourceProportion].value / 100
                    : 1;
                const workPortion = operations * operationsToWorkCoefficient * proportion;
                const resourceToWork = instantiateFactor(input.resourceToWork);
                const resourceToWorkValue: number = getValueFromFactor(resourceToWork, this._variables);
                const amount = workPortion * resourceToWorkValue;
                consumedResources[input.resource] =
                  consumedResources[input.resource] === undefined ? amount : consumedResources[input.resource] + amount;
              });
              work.output?.forEach((output) => {
                const workToResourceValue = getWorkToResourceCoefficient(output, this._variables);
                const amount = operations * operationsToWorkCoefficient * workToResourceValue;
                generatedResources[output.resource] =
                  generatedResources[output.resource] === undefined
                    ? amount
                    : generatedResources[output.resource] + amount;
              });
            });
          const workDatas: KeyMap<WorkData> = {};
          const cccInstance: CarbonCausalChain = this._cccs[ccc[0]];
          ccc[1].work.forEach((work) => {
            const workResources: KeyMap<WorkResourceData> = {};
            work.input.forEach((input) => {
              const { proportion, referenceProportion } =
                input.resourceProportion && this._variables[input.resourceProportion]
                  ? {
                      proportion: this._variables[input.resourceProportion].value,
                      referenceProportion: this._variables[input.resourceProportion].referenceValue,
                    }
                  : { proportion: 100, referenceProportion: 100 };
              workResources[input.resource] = {
                emissionFactor: cccInstance.getEmissionFactorOfInput(input, this._variables, false),
                referenceEmissionFactor: cccInstance.getEmissionFactorOfInput(input, this._variables, false, true),
                emissionFactorUnit: formatUnitAsSnakeCase(input.emissionFactor.unitOfMeasure) || '',
                proportion,
                referenceProportion,
                resourceToWork: getValueFromFactor(instantiateFactor(input.resourceToWork), this._variables),
                resourceToWorkUnit: formatUnitAsSnakeCase(input.resourceToWork.unitOfMeasure) || '',
              };
            });
            const workResourcesOutput: KeyMap<WorkOutputData> = {};
            work.output?.forEach((output) => {
              workResourcesOutput[output.resource] = {
                workToResource: getWorkToResourceCoefficient(output, this._variables),
              };
            });
            const workInstance: Work = cccInstance.work.find((w) => w.work === work.work);
            workDatas[work.work] = {
              workUnit: formatUnitAsSnakeCase(work.unitOfMeasure),
              emissionFactorReference: workInstance
                ? cccInstance.getWorkEmissionFactor(workInstance, this._variables, true)
                : 0,
              emissionFactor: workInstance
                ? cccInstance.getWorkEmissionFactor(workInstance, this._variables, false)
                : 0,
              energyIntensity: workInstance
                ? getValueFromFactor(<EvaluatedFactor>workInstance.operationToWork, this._variables)
                : 1,
              referenceEnergyIntensity: workInstance
                ? getValueFromFactor(<EvaluatedFactor>workInstance.operationToWork, this._variables, {
                    bau: true,
                  })
                : 1,
              energyIntensityUnit: formatUnitAsSnakeCase(work.operationToWork.unitOfMeasure),
              resources: workResources,
              outputs: workResourcesOutput,
            };
          });
          cccData[ccc[0]] = {
            consumedResources,
            generatedResources,
            carbonAbatement: cccInstance.carbonAbatementPerTE,
            bauEmissions: cccInstance.getEmissionsBau(t, this._variables),
            bauOperations: cccInstance.getOperationsBau(t),
            operations: cccInstance.operations,
            work: workDatas,
          };
        });
      return cccData;
    });
  }

  /**
   * Returns a KeyMap of the emissions caused cccs for a given year, indexed by ccc id.
   * @param t
   * @returns
   */
  private _getCccEmissionsBau(t: number, variables: KeyMap<Variable>): KeyMap<number> {
    const map = {};
    this._cccKeys.forEach((activeCcc) => {
      map[activeCcc] = this._cccs[activeCcc].getEmissionsBau(t, variables);
    });
    return map;
  }

  private _getCccEmissionsBauForInventory(inventoryId: string): KeyMap<number> {
    Object.keys(this._resourcesForInventory[inventoryId]).forEach((key) =>
      this._resourcesForInventory[inventoryId][key].reset()
    );
    this._cccKeysForInventory[inventoryId].forEach((cccId) => {
      this._cccsForInventory[inventoryId][cccId].calculateSupply(0, this._variablesForInventory[inventoryId]);
    });
    return this._cccKeysForInventory[inventoryId].reduce((acc, cccId) => {
      acc[cccId] = this._cccsForInventory[inventoryId][cccId].getEmissionsBau(
        0,
        this._variablesForInventory[inventoryId]
      );
      return acc;
    }, {});
  }

  private _getCccResourceEnergyConsumptionForInventory(inventoryId: string): KeyMap<KeyMap<number>> {
    return this._cccKeysForInventory[inventoryId].reduce((acc, cccId) => {
      acc[cccId] = this._cccsForInventory[inventoryId][cccId].getResourceConsumption(
        0,
        0,
        this._variablesForInventory[inventoryId]
      );
      return acc;
    }, {});
  }

  private _getEmissionFactorsForInventory(inventoryId: string): KeyMap<number> {
    Object.keys(this._resourcesForInventory[inventoryId]).forEach((key) =>
      this._resourcesForInventory[inventoryId][key].reset()
    );
    this._cccKeysForInventory[inventoryId].forEach((cccId) => {
      this._cccsForInventory[inventoryId][cccId].calculateSupply(0, this._variablesForInventory[inventoryId]);
    });
    const resources = this._getResourcesData(this._resourcesForInventory[inventoryId]);
    return {
      [ELECTRICITY]: resources[ELECTRICITY].emissionFactor,
      [DISTRICT_HEAT]: resources[DISTRICT_HEAT].emissionFactor,
    };
  }

  private _getResourcesData(resources: KeyMap<Resource>): KeyMap<ResourceData> {
    return this._tracingService.trace('_getResourcesData', () => {
      const resourceData: KeyMap<ResourceData> = {};
      Object.keys(resources).forEach((resourceKey) => {
        resourceData[resourceKey] = {
          amount: resources[resourceKey].amount,
          calculatedEmissionFactor: resources[resourceKey].combinedEmissionFactor,
          emissionFactor: resources[resourceKey].emissionFactor,
          emissionFactorBau: resources[resourceKey].emissionFactorBau,
          unit: resources[resourceKey].unit,
          isExclusivelyPrimaryProduced: resources[resourceKey].isExclusivelyPrimaryProduced,
          secondaryProductionData: resources[resourceKey].getSecondaryProductionData(),
          producedBy: resources[resourceKey].producedBy,
        };
      });
      return resourceData;
    });
  }

  // Returns Lhs ccc emissions, per subsector
  private _getSubsectorDividedEmissionData(t: number): KeyMap<SubsectorEmissionData> {
    return this._tracingService.trace('_getSubsectorDividedEmissionData', () => {
      const subsectorEmissionData: KeyMap<SubsectorEmissionData> = {};
      Object.keys(this._cccIdsActive).forEach((subsector) => {
        let bau = 0;
        let remaining = 0;
        [...this._cccIdsActive[subsector]].forEach((cccId) => {
          bau += this._cccs[cccId].getEmissionsBau(t, this._variables);
          remaining += this._cccs[cccId].calculateEmissionsFromOperations(
            this._cccs[cccId].operations,
            this._variables
          );
        });
        subsectorEmissionData[subsector] = { bau, remaining };
      });
      return subsectorEmissionData;
    });
  }

  private _getVariablesData(): KeyMap<VariableData> {
    return this._tracingService.trace('_getVariablesData', () => {
      const variablesData: KeyMap<VariableData> = {};
      Object.values(this._variables).forEach((variable) => {
        variablesData[variable.name] = {
          name: variable.name,
          unit: variable.unit,
          value: variable.value,
          referenceValue: variable.referenceValue,
        };
      });
      return variablesData;
    });
  }

  private _instantiateCarbonCausalChains(supplySideActivities: string[]): void {
    this._tracingService.trace('_instantiateCarbonCausalChains', () => {
      this._cccs = {};
      Object.keys(this._allCccs).forEach((key) => {
        if (key) {
          this._cccs[key] = new CarbonCausalChain(
            this._allCccs[key],
            this._variables,
            this._resources,
            supplySideActivities.includes(key)
          );
        }
      });
      this._cccKeys = Object.keys(this._cccs);
    });
  }

  private _instantiateCarbonCausalChainsForInventory(supplySideActivities: string[]): void {
    this._tracingService.trace('_instantiateCarbonCausalChainsForInventory', () => {
      this._cccsForInventory = {};
      Object.keys(this._variablesForInventory).forEach((inventoryId) => {
        Object.keys(this._resourcesForInventory[inventoryId]).forEach((key) =>
          this._resourcesForInventory[inventoryId][key].reset()
        );
        this._cccsForInventory[inventoryId] = {};
        Object.keys(this._allCccs).forEach((key) => {
          if (key) {
            this._cccsForInventory[inventoryId][key] = new CarbonCausalChain(
              this._allCccs[key],
              this._variablesForInventory[inventoryId],
              this._resourcesForInventory[inventoryId],
              supplySideActivities.includes(key)
            );
            this._cccsForInventory[inventoryId][key].resetOperations(0);
          }
        });
      });
      this._cccKeysForInventory = Object.keys(this._cccsForInventory).reduce((acc, inventoryId) => {
        acc[inventoryId] = Object.keys(this._cccsForInventory[inventoryId]);
        return acc;
      }, {});
    });
  }

  private _instantiateParameters(variableData: BoardParametersV2): void {
    this._tracingService.trace('_instantiateParameters', () => {
      const variables = this._modelService.variables;
      this._variables = {};
      Object.keys(variables).forEach((key) => {
        const nameId = variables[key]?.variable;
        this._variables[key] = new Variable(variables[key], variableData[nameId]);
      });
    });
  }

  private _instantiateParametersForInventory(inventoriesOverride: { [inventoryId: string]: BoardParametersV2 }): void {
    this._tracingService.trace('_instantiateParametersForInventory', () => {
      const variables = this._modelService.variables;
      this._variablesForInventory = {};
      Object.keys(inventoriesOverride).forEach((inventoryId) => {
        this._variablesForInventory[inventoryId] = {};
        Object.keys(variables).forEach((key) => {
          const nameId = variables[key]?.variable;
          this._variablesForInventory[inventoryId][key] = new Variable(
            variables[key],
            inventoriesOverride[inventoryId][nameId]
          );
        });
      });
    });
  }

  private _instantiateResources(resourceNames: string[], overridingEmissionFactors: KeyMap<Variable>): void {
    this._tracingService.trace('_instantiateResources', () => {
      this._resources = {};
      resourceNames.forEach((key) => {
        let defaultEmissionFactorBau: string;
        // @TODO: this parameter reference should be an attribute on the resource object if ResourceType is GridSuppliedEnergy
        let calculateEmissionFactorsUsingSupplySideTEs = false;
        if (this._allResources[key].type === ResourceType.GridSuppliedEnergy) {
          if (key === ELECTRICITY) {
            defaultEmissionFactorBau = 'emission_factor_electricity_current';
            calculateEmissionFactorsUsingSupplySideTEs = true;
          } else if (key === DISTRICT_HEAT) {
            defaultEmissionFactorBau = 'emission_factor_district_heating_kwh_to_co2e';
            calculateEmissionFactorsUsingSupplySideTEs = true;
          }
        }
        this._resources[key] = new Resource(
          this._allResources[key],
          this._variables[defaultEmissionFactorBau]?.value,
          !calculateEmissionFactorsUsingSupplySideTEs,
          overridingEmissionFactors[key]
        );
      });
    });
  }

  private _instantiateResourcesForInventory(
    resourceNames: string[],
    overridingEmissionFactors: KeyMap<Variable>
  ): void {
    this._tracingService.trace('_instantiateResourcesForInventory', () => {
      this._resourcesForInventory = {};
      Object.keys(this._variablesForInventory).forEach((inventoryId) => {
        this._resourcesForInventory[inventoryId] = {};
        resourceNames.forEach((key) => {
          let defaultEmissionFactorBau: string;
          let calculateEmissionFactorsUsingSupplySideTEs = false;
          if (this._allResources[key].type === ResourceType.GridSuppliedEnergy) {
            if (key === ELECTRICITY) {
              defaultEmissionFactorBau = 'emission_factor_electricity_current';
              calculateEmissionFactorsUsingSupplySideTEs = true;
            } else if (key === DISTRICT_HEAT) {
              defaultEmissionFactorBau = 'emission_factor_district_heating_kwh_to_co2e';
              calculateEmissionFactorsUsingSupplySideTEs = true;
            }
          }
          const overridingEmissionFactor = overridingEmissionFactors[key]
            ? this._variablesForInventory[inventoryId][overridingEmissionFactors[key].name]
            : undefined;
          this._resourcesForInventory[inventoryId][key] = new Resource(
            this._allResources[key],
            this._variablesForInventory[inventoryId][defaultEmissionFactorBau]?.value,
            !calculateEmissionFactorsUsingSupplySideTEs,
            overridingEmissionFactor
          );
        });
      });
    });
  }

  private _instantiateStocks(): void {
    this._tracingService.trace('_instantiateStocks', () => {
      this._stocks = {};
      Object.keys(this._allStocks).forEach((key) => {
        this._stocks[key] = new Stock(this._allStocks[key], this._variables);
      });
    });
  }

  private _instantiateTransitionElements(transitionElements: TransitionElement[]): void {
    this._tracingService.trace('_instantiateTransitionElements', () => {
      this._transitionElements = {};
      transitionElements.forEach(
        (key) =>
          (this._transitionElements[key] = new TransitionTarget(
            this._allTransitionElements[key],
            this._cccs,
            this._cccModelUtility
          ))
      );
      this._ttKeys = Object.keys(this._transitionElements);
    });
  }

  private _invertResourceShiftChanges(
    t: number,
    transitions: KeyMap<TransitionData>,
    transitionWeights: TargetWeights
  ) {
    this._tracingService.trace('_invertResourceShiftChanges', () => {
      this._resourceTtKeys.forEach((ttKey) =>
        this._transitionElements[ttKey].invertResourceShifts(
          transitions[ttKey].newWeight || transitionWeights[ttKey].value,
          this._variables,
          this._resources
        )
      );
    });
  }

  private _populateCccIdSets(): void {
    this._tracingService.trace('_populateCccIdSets', () => {
      this._cccIdsActive = {};
      this._ttKeysInOrder.forEach((ttKey) => {
        const subsector = this._transitionElements[ttKey].subsector;
        if (!this._cccIdsActive[subsector]) {
          this._cccIdsActive[subsector] = new Set<string>();
        }
        this._transitionElements[ttKey].lhsCcc.forEach((lhsCccId) => this._cccIdsActive[subsector].add(lhsCccId));
        this._transitionElements[ttKey].rhsCcc.forEach((rhsCccId) => this._cccIdsActive[subsector].add(rhsCccId));
      });
    });
  }

  private _resetModel(tSetup: number, tReport: number): void {
    this._tracingService.trace('_resetModel', () => {
      this._cccKeys.forEach((key) => {
        this._cccs[key].resetOperations(tSetup, tReport);
        this._cccs[key].resetCarbonAbatement();
      });
      Object.keys(this._variables).forEach((key) => this._variables[key].resetParameter());
      Object.keys(this._resources).forEach((key) => this._resources[key].reset());
    });
  }

  private _setActivityToCarbonAbatementFactors(transitions: KeyMap<TransitionData>) {
    this._tracingService.trace('_setActivityToCarbonAbatementFactors', () => {
      Object.keys(transitions).forEach((key) => {
        const transition = transitions[key];
        const transitionType = this._cccModelUtility.getTransitionType(key as TransitionElement);
        const activityShift =
          transitionType === TransitionElementType.ResourceShift
            ? transition.activityShift - (transition as TransitionResourceShiftData).proportionStart
            : transition.activityShift;
        const carbonAbatement =
          transitionType === TransitionElementType.Shift
            ? (transition as TransitionShiftData).carbonAbatement.net
            : transition.carbonAbatement.gross;
        if (activityShift === 0) {
          transition.activityToCarbonAbatementFactor = undefined;
        } else {
          transition.activityToCarbonAbatementFactor = carbonAbatement / activityShift;
        }
      });
    });
  }

  private _setParameterRelatedValues(ttKey: string, t: number, weight: number, transitions: KeyMap<TransitionData>) {
    this._tracingService.trace('_setParameterRelatedValues', () => {
      const {
        currentParameterValue,
        parameterPotential,
        grossCarbonAbatement,
        unaffectedParameterValue,
        capParameterValue,
        carbonAbatementPerCcc,
        operationsFullyAffected,
        referenceEnergyIntensity,
        referenceEnergyIntensityPerCcc,
        maxUpdatedEnergyIntensity,
        weightedEmissionFactor,
      } = this._transitionElements[ttKey].computeUpdateAfterFinish(weight, this._variables);
      if (this._transitionElements[ttKey].transitionType === TransitionElementType.Update) {
        transitions[ttKey].carbonAbatement.gross = grossCarbonAbatement;
        transitions[ttKey].potential.unconstrained = this._transitionElements[ttKey].getPotential(
          t,
          weight,
          this._variables
        ).potential;
        Object.keys(carbonAbatementPerCcc).forEach((cccId) => {
          this._cccs[cccId].registerCarbonAbatement(ttKey as TransitionElement, carbonAbatementPerCcc[cccId]);
        });
        (<TransitionUpdateData>transitions[ttKey]).operationsFullyAffected = operationsFullyAffected;
        (<TransitionUpdateData>transitions[ttKey]).referenceEnergyIntensity = referenceEnergyIntensity;
        (<TransitionUpdateData>transitions[ttKey]).referenceEnergyIntensityPerCcc = referenceEnergyIntensityPerCcc;
        (<TransitionUpdateData>transitions[ttKey]).maxUpdatedEnergyIntensity = maxUpdatedEnergyIntensity;
        (<TransitionUpdateData>transitions[ttKey]).weightedEmissionFactor = weightedEmissionFactor;
      }
      (<ParameterTransition>transitions[ttKey]).currentParameterValue = currentParameterValue;
      (<ParameterTransition>transitions[ttKey]).parameterPotential = parameterPotential;
      (<ParameterTransition>transitions[ttKey]).unaffectedParameterValue = unaffectedParameterValue;
      (<ParameterTransition>transitions[ttKey]).capParameterValue = capParameterValue;
      (<ParameterTransition>transitions[ttKey]).transitionDirection =
        capParameterValue < unaffectedParameterValue ? TransitionUpdateDirection.Down : TransitionUpdateDirection.Up;
    });
  }

  private _setPotential(t: number, transitions: KeyMap<TransitionData>, transitionWeights: TargetWeights): void {
    this._tracingService.trace('_setPotential', () => {
      this._ttKeysInOrder.forEach((ttKey) => {
        const { potential, unconstrainedPotential } = this._transitionElements[ttKey].getPotential(
          t,
          transitions[ttKey].newWeight || transitionWeights[ttKey].value,
          this._variables
        );
        if (this._transitionElements[ttKey].transitionType === TransitionElementType.Update) {
          transitions[ttKey].potential.constrained = potential;
        } else if (this._transitionElements[ttKey].transitionType === TransitionElementType.ResourceShift) {
          transitions[ttKey].potential = {
            constrained: potential,
            unconstrained: potential,
          };
        } else if (
          this._transitionElements[ttKey].transitionType === TransitionElementType.Shift ||
          this._transitionElements[ttKey].transitionType === TransitionElementType.Upshift
        ) {
          transitions[ttKey].potential = {
            constrained: potential,
            unconstrained: unconstrainedPotential,
          };
        }
      });
    });
  }

  private _setupEnvironment(t: number): void {
    this._tracingService.trace('_setupEnvironment', () => {
      Object.keys(this._cccs).forEach((key) => this._cccs[key].calculateSupply(t, this._variables));
    });
  }
}
